import { Component, Element, h, Host, Prop, State } from '@stencil/core';
import { close_small } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-app-banner',
  styleUrl: 'pn-app-banner.scss',
})
export class PnAppBanner {
  @Element() hostElement: HTMLElement;
  @Prop() appName: string;
  @Prop() appDescription: string;
  @Prop() ctaText: string;
  @Prop() appleStoreLink: string;
  @Prop() googleStoreLink: string;

  @State() isClosed: boolean = false;
  @State() storeLink: string;
  @State() isLocalStorageAvailable = true;

  CLOSED_STORAGE_KEY = 'SmartAppBannerClosed';
  CLOSED_COOKIE_EXPIRY_DAYS = 7;

  checkLocalStorage() {
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
    } catch (e) {
      this.isLocalStorageAvailable = false;
    }
  }

  detectDeviceAndSetStoreLink() {
    const userAgent = navigator.userAgent;

    if (userAgent.match(/Android/i)) {
      this.storeLink = this.googleStoreLink;
    } else if (userAgent.match(/iPhone|iPad|iPod/i)) {
      this.storeLink = this.appleStoreLink;
    } else {
      this.storeLink = null;
      this.isClosed = true; // -> data-is-closed = true
    }
  }

  handleOnClose = () => {
    this.isClosed = true;

    // Handle storage based on availability
    if (this.isLocalStorageAvailable) {
      localStorage.setItem(this.CLOSED_STORAGE_KEY, 'true');
    } else {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + this.CLOSED_COOKIE_EXPIRY_DAYS);
      document.cookie = `${this.CLOSED_STORAGE_KEY}=true; expires=${expirationDate.toUTCString()}`;
    }
  };

  componentWillLoad() {
    this.checkLocalStorage();
    // Handle storage based on availability
    if (this.isLocalStorageAvailable) {
      this.isClosed = localStorage.getItem(this.CLOSED_STORAGE_KEY) === 'true';
    } else {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === this.CLOSED_STORAGE_KEY && value === 'true') {
          this.isClosed = true;
          break;
        }
      }
    }

    if (!this.isClosed) {
      this.detectDeviceAndSetStoreLink();
    }
  }

  render() {
    return (
      <Host data-is-closed={this.isClosed ? 'true' : 'false'}>
        <div class="pn-app-banner__wrapper pn-app-banner__sticky">
          <div class="icon-close" onClick={this.handleOnClose}>
            <pn-icon icon={close_small} small="false" color="black" />
          </div>
          <div class="pn-app-banner__icon">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_17800_45416)">
                <path
                  d="M23.875 0H8.125C3.63769 0 0 3.63769 0 8.125V23.875C0 28.3623 3.63769 32 8.125 32H23.875C28.3623 32 32 28.3623 32 23.875V8.125C32 3.63769 28.3623 0 23.875 0Z"
                  fill="#F18D8D"
                />
                <mask id="mask0_17800_45416" style={{ 'mask-type': 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
                  <path
                    d="M23.875 0H8.125C3.63769 0 0 3.63769 0 8.125V23.875C0 28.3623 3.63769 32 8.125 32H23.875C28.3623 32 32 28.3623 32 23.875V8.125C32 3.63769 28.3623 0 23.875 0Z"
                    fill="#0D234B"
                  />
                </mask>
                <g mask="url(#mask0_17800_45416)">
                  <path
                    d="M23.875 0H8.125C3.63769 0 0 3.63769 0 8.125V23.875C0 28.3623 3.63769 32 8.125 32H23.875C28.3623 32 32 28.3623 32 23.875V8.125C32 3.63769 28.3623 0 23.875 0Z"
                    fill="#0D234B"
                  />
                  <path d="M50.8125 14.1562H18.8125V46.1562H50.8125V14.1562Z" fill="url(#paint0_linear_17800_45416)" />
                  <path
                    d="M2.09375 31.375C2.09375 15.2379 15.1754 2.15625 31.3125 2.15625H31.9844V31.9844H2.09998C2.09583 31.7819 2.09375 31.5788 2.09375 31.375Z"
                    fill="#00A0D6"
                  />
                  <g filter="url(#filter0_d_17800_45416)">
                    <path
                      d="M7.40625 11.8062C7.40625 10.2661 7.40625 9.49603 7.70597 8.90778C7.96963 8.39034 8.39034 7.96963 8.90778 7.70597C9.49603 7.40625 10.2661 7.40625 11.8062 7.40625H20.1938C21.7339 7.40625 22.504 7.40625 23.0922 7.70597C23.6097 7.96963 24.0304 8.39034 24.294 8.90778C24.5938 9.49603 24.5938 10.2661 24.5938 11.8062V20.1938C24.5938 21.7339 24.5938 22.504 24.294 23.0922C24.0304 23.6097 23.6097 24.0304 23.0922 24.294C22.504 24.5938 21.7339 24.5938 20.1938 24.5938H11.8062C10.2661 24.5938 9.49603 24.5938 8.90778 24.294C8.39034 24.0304 7.96963 23.6097 7.70597 23.0922C7.40625 22.504 7.40625 21.7339 7.40625 20.1938V11.8062Z"
                      fill="#F1F7FA"
                    />
                    <path
                      opacity="0.6"
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M10.5938 10.0625C10.3176 10.0625 10.0938 10.2863 10.0938 10.5625C10.0938 10.8387 10.3176 11.0625 10.5938 11.0625H12.875C13.1512 11.0625 13.375 10.8387 13.375 10.5625C13.375 10.2863 13.1512 10.0625 12.875 10.0625H10.5938ZM10.5938 12.3125C10.3176 12.3125 10.0938 12.5363 10.0938 12.8125C10.0938 13.0887 10.3176 13.3125 10.5938 13.3125H15.3125C15.5887 13.3125 15.8125 13.0887 15.8125 12.8125C15.8125 12.5363 15.5887 12.3125 15.3125 12.3125H10.5938ZM10.0938 14.75C10.0938 14.4738 10.3176 14.25 10.5938 14.25H13.5625C13.8387 14.25 14.0625 14.4738 14.0625 14.75C14.0625 15.0262 13.8387 15.25 13.5625 15.25H10.5938C10.3176 15.25 10.0938 15.0262 10.0938 14.75ZM15.1875 14.25C14.9113 14.25 14.6875 14.4738 14.6875 14.75C14.6875 15.0262 14.9113 15.25 15.1875 15.25C15.4637 15.25 15.6875 15.0262 15.6875 14.75C15.6875 14.4738 15.4637 14.25 15.1875 14.25Z"
                      fill="#A5C0CA"
                    />
                    <path
                      d="M17.9375 10.5938C17.9375 10.3176 18.1613 10.0938 18.4375 10.0938H21.75C22.0262 10.0938 22.25 10.3176 22.25 10.5938C22.25 10.8699 22.0262 11.0938 21.75 11.0938H18.4375C18.1613 11.0938 17.9375 10.8699 17.9375 10.5938Z"
                      fill="#00A0D6"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M10.0938 19.3125C10.0938 18.8983 10.4295 18.5625 10.8438 18.5625C11.258 18.5625 11.5938 18.8983 11.5938 19.3125V21.25C11.5938 21.6642 11.258 22 10.8438 22C10.4295 22 10.0938 21.6642 10.0938 21.25V19.3125ZM19.3438 19.3125C19.3438 18.8983 19.6795 18.5625 20.0938 18.5625C20.508 18.5625 20.8438 18.8983 20.8438 19.3125V21.25C20.8438 21.6642 20.508 22 20.0938 22C19.6795 22 19.3438 21.6642 19.3438 21.25V19.3125ZM14.5938 18.5625C14.1795 18.5625 13.8438 18.8983 13.8438 19.3125V21.25C13.8438 21.6642 14.1795 22 14.5938 22C15.008 22 15.3438 21.6642 15.3438 21.25V19.3125C15.3438 18.8983 15.008 18.5625 14.5938 18.5625ZM12.5938 18.9375C12.5938 18.7304 12.7617 18.5625 12.9688 18.5625C13.1758 18.5625 13.3438 18.7304 13.3438 18.9375V21.625C13.3438 21.8321 13.1758 22 12.9688 22C12.7617 22 12.5938 21.8321 12.5938 21.625V18.9375ZM21.5938 18.5625C21.3867 18.5625 21.2188 18.7304 21.2188 18.9375V21.625C21.2188 21.8321 21.3867 22 21.5938 22C21.8008 22 21.9688 21.8321 21.9688 21.625V18.9375C21.9688 18.7304 21.8008 18.5625 21.5938 18.5625ZM17.5938 18.9375C17.5938 18.7304 17.7617 18.5625 17.9688 18.5625C18.1758 18.5625 18.3438 18.7304 18.3438 18.9375V21.625C18.3438 21.8321 18.1758 22 17.9688 22C17.7617 22 17.5938 21.8321 17.5938 21.625V18.9375ZM16.5938 18.5625C16.3176 18.5625 16.0938 18.7863 16.0938 19.0625V21.5C16.0938 21.7762 16.3176 22 16.5938 22C16.8699 22 17.0938 21.7762 17.0938 21.5V19.0625C17.0938 18.7863 16.8699 18.5625 16.5938 18.5625Z"
                      fill="#0D234B"
                    />
                  </g>
                </g>
              </g>
              <defs>
                <filter id="filter0_d_17800_45416" x="-52.5938" y="-48.5938" width="137.188" height="137.188" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="30" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_17800_45416" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_17800_45416" result="shape" />
                </filter>
                <linearGradient id="paint0_linear_17800_45416" x1="29.2305" y1="44.3441" x2="51.5694" y2="15.1782" gradientUnits="userSpaceOnUse">
                  <stop offset="0.0800778" stop-color="#0D244C" />
                  <stop offset="1" stop-color="#0D234B" />
                </linearGradient>
                <clipPath id="clip0_17800_45416">
                  <rect width="32" height="32" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div class="pn-app-banner__text">
            <span>{this.appName}</span>
            <br />
            {this.appDescription}
          </div>
          <div class="pn-app-banner__cta">
            <pn-button small={true} href={this.storeLink}>
              {this.ctaText}
            </pn-button>
          </div>
        </div>
      </Host>
    );
  }
}
